import React, { useState } from 'react';
import axios from 'axios';
import {
  Shield, AlertTriangle, AlertCircle, Info, Loader2, RefreshCw, Wrench,
  Files, Copy, CalendarX, CalendarClock, Users, Inbox, Minus, CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const h = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const CATEGORIES = [
  {
    key: 'duplicate_documents',
    label: 'Duplicate Documents',
    severity: 'critical',
    icon: Files,
    desc: 'Same channel+month exists as multiple DB documents — root cause of 2× / 3× inflation in reports.',
    autoFixable: true,
  },
  {
    key: 'duplicate_entries',
    label: 'Duplicate Entries (same date)',
    severity: 'warning',
    icon: Copy,
    desc: 'Same date appears more than once within a marketing/sales/operation array.',
    autoFixable: true,
  },
  {
    key: 'future_dates',
    label: 'Future-Month Dates',
    severity: 'critical',
    icon: CalendarX,
    desc: 'Entries with date AFTER current month — likely typo or data masuk salah bulan.',
    autoFixable: true,
  },
  {
    key: 'cross_month_dates',
    label: 'Cross-Month Date Mismatch',
    severity: 'warning',
    icon: CalendarClock,
    desc: 'Entry date does NOT match its parent document month (e.g. June entry stored under May doc).',
    autoFixable: true,
  },
  {
    key: 'orphan_brokers',
    label: 'Orphan Broker Keys',
    severity: 'info',
    icon: Users,
    desc: 'Sales data references broker key NOT in IB\'s enabled broker_config — case mismatch or stale data.',
    autoFixable: false,
  },
  {
    key: 'empty_docs',
    label: 'Empty Documents',
    severity: 'info',
    icon: Inbox,
    desc: 'Docs with 0 marketing/sales/operation entries — clutter, safe to delete.',
    autoFixable: true,
  },
  {
    key: 'negative_values',
    label: 'Negative Values',
    severity: 'warning',
    icon: Minus,
    desc: 'Commission/lots/active_client/deposit with negative numbers — likely data entry error.',
    autoFixable: false,
  },
];

export default function DataQualityDashboard() {
  const [scanning, setScanning] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [report, setReport] = useState(null);
  const [expandedCat, setExpandedCat] = useState(null);
  const [selectedFixes, setSelectedFixes] = useState({});

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await axios.get(`${API}/admin/data-quality/scan`, h());
      setReport(res.data);
      // Auto-select fixable categories that have findings
      const sel = {};
      CATEGORIES.forEach(c => {
        if (c.autoFixable && (res.data.findings[c.key]?.length || 0) > 0) {
          sel[c.key] = true;
        }
      });
      setSelectedFixes(sel);
      const total = Object.values(res.data.severity_counts).reduce((a, b) => a + b, 0);
      toast.success(`Scan complete: ${total} findings across ${res.data.total_docs_scanned} docs`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed (must be workspace owner)');
    } finally {
      setScanning(false);
    }
  };

  const runAutoFix = async () => {
    const categories = Object.keys(selectedFixes).filter(k => selectedFixes[k]);
    if (categories.length === 0) {
      toast.error('Select at least one fix category');
      return;
    }
    if (!window.confirm(`Auto-fix ${categories.length} categor${categories.length === 1 ? 'y' : 'ies'}?\n\nThis will permanently modify the DB. Items in non-fixable categories (orphan brokers, negatives) need manual review.`)) {
      return;
    }
    setFixing(true);
    try {
      const res = await axios.post(`${API}/admin/data-quality/auto-fix`, { fix: categories }, h());
      const c = res.data.counts;
      const total = Object.values(c).reduce((a, b) => a + b, 0);
      toast.success(`Fixed ${total} issues: docs merged=${c.duplicate_documents_merged}, entries deduped=${c.entries_deduped}, future dates removed=${c.future_dates_removed}, cross-month removed=${c.cross_month_dates_removed}, empty docs deleted=${c.empty_docs_deleted}`);
      // Re-scan to show clean state
      await runScan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-fix failed');
    } finally {
      setFixing(false);
    }
  };

  const sev = report?.severity_counts || { critical: 0, warning: 0, info: 0 };
  const allClean = report && (sev.critical + sev.warning + sev.info === 0);

  return (
    <div className="space-y-4" data-testid="data-quality-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-700 flex items-center justify-center">
            <Shield size={18} className="text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Data Quality Dashboard</h2>
            <p className="text-xs text-[hsl(var(--text-muted))]">Scan & auto-fix data anomalies across all channels</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={runScan} disabled={scanning} className="bg-blue-600 hover:bg-blue-700" data-testid="scan-btn">
            {scanning ? <Loader2 size={14} className="animate-spin mr-2" /> : <RefreshCw size={14} className="mr-2" />}
            Run Scan
          </Button>
          {report && Object.values(selectedFixes).some(v => v) && (
            <Button onClick={runAutoFix} disabled={fixing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="autofix-btn">
              {fixing ? <Loader2 size={14} className="animate-spin mr-2" /> : <Wrench size={14} className="mr-2" />}
              Auto-Fix Selected
            </Button>
          )}
        </div>
      </div>

      {report && (
        <>
          {/* Severity summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle size={14} className="text-red-400" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-red-400">Critical</span>
              </div>
              <div className="text-3xl font-bold text-red-400 font-mono">{sev.critical}</div>
              <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1">Causes inflated totals in reports</p>
            </div>
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle size={14} className="text-amber-400" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-amber-400">Warning</span>
              </div>
              <div className="text-3xl font-bold text-amber-400 font-mono">{sev.warning}</div>
              <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1">Suspicious, needs review</p>
            </div>
            <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Info size={14} className="text-blue-400" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-blue-400">Info</span>
              </div>
              <div className="text-3xl font-bold text-blue-400 font-mono">{sev.info}</div>
              <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1">Low-impact, optional cleanup</p>
            </div>
          </div>

          <div className="text-[11px] text-[hsl(var(--text-muted))] flex flex-wrap gap-3">
            <span>Scanned: <span className="text-white font-mono">{report.total_docs_scanned}</span> docs</span>
            <span>·</span>
            <span>IBs: <span className="text-white font-mono">{report.ibs_scanned}</span></span>
            <span>·</span>
            <span>At: <span className="text-white font-mono">{new Date(report.scanned_at).toLocaleString('en-MY')}</span></span>
          </div>

          {allClean ? (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-6 text-center">
              <CheckCircle2 size={36} className="text-emerald-400 mx-auto mb-2" />
              <h3 className="text-lg font-bold text-emerald-400">All Clean — No Anomalies Detected</h3>
              <p className="text-xs text-[hsl(var(--text-muted))] mt-1">Data integrity is in good shape across all channels.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {CATEGORIES.map(cat => {
                const findings = report.findings[cat.key] || [];
                if (findings.length === 0) return null;
                const Icon = cat.icon;
                const sevColor = cat.severity === 'critical' ? '#ef4444' : cat.severity === 'warning' ? '#f59e0b' : '#3b82f6';
                const isExpanded = expandedCat === cat.key;
                return (
                  <div key={cat.key} className="rounded-lg border bg-[hsl(var(--card))]" style={{ borderColor: `${sevColor}30` }}>
                    <button
                      onClick={() => setExpandedCat(isExpanded ? null : cat.key)}
                      className="w-full flex items-center gap-3 p-3 text-left hover:bg-[hsl(var(--surface))] transition-colors"
                      data-testid={`cat-${cat.key}`}
                    >
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${sevColor}15` }}>
                        <Icon size={16} style={{ color: sevColor }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-bold text-white">{cat.label}</span>
                          <Badge style={{ background: `${sevColor}15`, color: sevColor, borderColor: `${sevColor}40` }} className="border text-[9px] uppercase tracking-wider">
                            {cat.severity}
                          </Badge>
                          <span className="text-[11px] font-mono text-white ml-auto">{findings.length}</span>
                        </div>
                        <p className="text-[11px] text-[hsl(var(--text-muted))] leading-relaxed">{cat.desc}</p>
                      </div>
                      {cat.autoFixable && (
                        <label className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--text-muted))] cursor-pointer" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={!!selectedFixes[cat.key]}
                            onChange={() => setSelectedFixes(s => ({ ...s, [cat.key]: !s[cat.key] }))}
                            className="w-3.5 h-3.5"
                            data-testid={`fix-toggle-${cat.key}`}
                          />
                          Auto-fix
                        </label>
                      )}
                    </button>
                    {isExpanded && (
                      <div className="border-t border-[hsl(var(--border))] p-3 max-h-[320px] overflow-y-auto">
                        <table className="w-full text-[11px]">
                          <thead className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))]">
                            <tr>
                              {cat.key === 'duplicate_documents' && (
                                <>
                                  <th className="text-left py-1">IB</th>
                                  <th className="text-left py-1">Month</th>
                                  <th className="text-right py-1">Doc Count</th>
                                </>
                              )}
                              {cat.key === 'duplicate_entries' && (
                                <>
                                  <th className="text-left py-1">IB</th>
                                  <th className="text-left py-1">Month</th>
                                  <th className="text-left py-1">Array</th>
                                  <th className="text-left py-1">Duplicates</th>
                                </>
                              )}
                              {(cat.key === 'future_dates' || cat.key === 'cross_month_dates') && (
                                <>
                                  <th className="text-left py-1">IB</th>
                                  <th className="text-left py-1">Month</th>
                                  <th className="text-left py-1">Array</th>
                                  <th className="text-left py-1">Date</th>
                                </>
                              )}
                              {cat.key === 'orphan_brokers' && (
                                <>
                                  <th className="text-left py-1">IB</th>
                                  <th className="text-left py-1">Month</th>
                                  <th className="text-left py-1">Date</th>
                                  <th className="text-left py-1">Broker Key</th>
                                </>
                              )}
                              {cat.key === 'empty_docs' && (
                                <>
                                  <th className="text-left py-1">IB</th>
                                  <th className="text-left py-1">Month</th>
                                </>
                              )}
                              {cat.key === 'negative_values' && (
                                <>
                                  <th className="text-left py-1">IB</th>
                                  <th className="text-left py-1">Month</th>
                                  <th className="text-left py-1">Date</th>
                                  <th className="text-left py-1">Broker</th>
                                  <th className="text-left py-1">Field</th>
                                  <th className="text-right py-1">Value</th>
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {findings.map((f, i) => (
                              <tr key={i} className="border-t border-[hsl(var(--border))]/50">
                                {cat.key === 'duplicate_documents' && (
                                  <>
                                    <td className="py-1 font-mono text-white">{f.ib_name}</td>
                                    <td className="py-1">{f.month}</td>
                                    <td className="py-1 text-right font-mono text-red-400 font-bold">×{f.doc_count}</td>
                                  </>
                                )}
                                {cat.key === 'duplicate_entries' && (
                                  <>
                                    <td className="py-1 font-mono text-white">{f.ib_name}</td>
                                    <td className="py-1">{f.month}</td>
                                    <td className="py-1 capitalize">{f.array}</td>
                                    <td className="py-1 text-[10px] text-amber-400">{f.duplicates?.join(', ')}{f.total_duplicate_dates > 10 ? ` (+${f.total_duplicate_dates - 10} more)` : ''}</td>
                                  </>
                                )}
                                {(cat.key === 'future_dates' || cat.key === 'cross_month_dates') && (
                                  <>
                                    <td className="py-1 font-mono text-white">{f.ib_name}</td>
                                    <td className="py-1">{f.month}</td>
                                    <td className="py-1 capitalize">{f.array}</td>
                                    <td className="py-1 font-mono text-red-400">{f.date}</td>
                                  </>
                                )}
                                {cat.key === 'orphan_brokers' && (
                                  <>
                                    <td className="py-1 font-mono text-white">{f.ib_name}</td>
                                    <td className="py-1">{f.month}</td>
                                    <td className="py-1">{f.date}</td>
                                    <td className="py-1 font-mono text-blue-400">{f.orphan_broker_key}</td>
                                  </>
                                )}
                                {cat.key === 'empty_docs' && (
                                  <>
                                    <td className="py-1 font-mono text-white">{f.ib_name}</td>
                                    <td className="py-1">{f.month}</td>
                                  </>
                                )}
                                {cat.key === 'negative_values' && (
                                  <>
                                    <td className="py-1 font-mono text-white">{f.ib_name}</td>
                                    <td className="py-1">{f.month}</td>
                                    <td className="py-1">{f.date}</td>
                                    <td className="py-1">{f.broker}</td>
                                    <td className="py-1">{f.field}</td>
                                    <td className="py-1 text-right font-mono text-amber-400">{f.value}</td>
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {!cat.autoFixable && (
                          <p className="text-[10px] text-blue-400 mt-2 italic">
                            ⓘ This category needs manual review — auto-fix not available. Investigate the source data and fix in Daily Data UI.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {!report && !scanning && (
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-8 text-center">
          <Shield size={36} className="text-[hsl(var(--text-muted))] mx-auto mb-3 opacity-50" />
          <h3 className="text-sm font-bold text-white mb-1">No scan results yet</h3>
          <p className="text-[11px] text-[hsl(var(--text-muted))]">Click <b>Run Scan</b> to detect anomalies across all channels.</p>
        </div>
      )}
    </div>
  );
}
